'use client';

import { useCallback, useRef } from 'react';

export default function RangeSlider({
  min,
  max,
  value,
  onChange,
  formatValue,
  disabled,
  className,
}: {
  min: number,
  max: number,
  value: [number, number],
  onChange: (range: [number, number]) => void,
  formatValue?: (v: number) => string,
  disabled?: boolean,
  className?: string,
}) {
  const format = formatValue ?? ((v: number) => String(v));
  const trackRef = useRef<HTMLDivElement>(null);

  const collapsed = min === max;
  const inputMin = collapsed ? 0 : min;
  const inputMax = collapsed ? 1 : max;
  const inputValMin = collapsed ? 0 : value[0];
  const inputValMax = collapsed ? 1 : value[1];

  const range = inputMax - inputMin || 1;
  const leftPct = ((inputValMin - inputMin) / range) * 100;
  const rightPct = ((inputValMax - inputMin) / range) * 100;

  const onMinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseInt(e.target.value, 10);
    // Push max if min catches up
    const newMin = Math.min(v, inputMax);
    const newMax = Math.max(newMin, value[1]);
    onChange([newMin, newMax,]);
  }, [value, onChange, inputMax,]);

  const onMaxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseInt(e.target.value, 10);
    // Push min if max catches up
    const newMax = Math.max(v, inputMin);
    const newMin = Math.min(value[0], newMax);
    onChange([newMin, newMax,]);
  }, [value, onChange, inputMin,]);

  // Min input on top when closer to max end, so it's always grabbable
  const minOnTop = inputValMin > (inputMin + inputMax) / 2;

  return (
    <div className={`range-slider ${disabled ? `range-slider-disabled` : ``} ${className ?? ``}`}>
      <span className="range-slider-value">{format(value[0])}</span>
      <div className="range-slider-track" ref={trackRef}>
        <div
          className="range-slider-fill"
          style={{ left: `${leftPct}%`, right: `${100 - rightPct}%`, }}
        />
        <input
          type="range"
          min={inputMin}
          max={inputMax}
          value={inputValMin}
          onChange={onMinChange}
          disabled={disabled || collapsed}
          className="range-slider-input"
          style={minOnTop ? { zIndex: 2, } : undefined}
        />
        <input
          type="range"
          min={inputMin}
          max={inputMax}
          value={inputValMax}
          onChange={onMaxChange}
          disabled={disabled || collapsed}
          className="range-slider-input"
        />
      </div>
      <span className="range-slider-value">{format(value[1])}</span>
    </div>
  );
}
